import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { validateJtToken } from '../api/auth.api';

const POLL_INTERVAL_MS = 5 * 60 * 1000;   // 5 minutes
const STARTUP_DELAY_MS = 10 * 1000;       // wait 10s after mount before first check

/**
 * Background watchdog: every 5 min, pings JT (via our backend) to confirm
 * the stored JT token is still valid. On a definitive 401/403, clears auth
 * and bounces the user to the login page so they refresh from JT and
 * don't sit in Engage assuming everything works.
 *
 * Behaviour rules:
 *  - Only runs when we have BOTH an Engage JWT and a cached JT token.
 *    Without the JT token there's nothing to validate (e.g. takeover
 *    sessions, or sessions established before the cache was added).
 *  - "Don't know" responses (transient network errors, JT 5xx) leave the
 *    session alone — we'd rather over-stay than over-logout on a blip.
 *  - On definitive expiry, also pops a toast-y window.alert so the user
 *    knows WHY they got bounced — silent redirect is hostile UX.
 *
 * Long-term path: when the per-org JT_API_KEY pattern from Section 11.1
 * lands, user JT tokens won't be needed for server-side sync at all and
 * this watchdog only matters for user-initiated JT-touching actions.
 */
export function useJtTokenWatchdog() {
  const engageToken = useAuthStore((s) => s.token);
  const jtToken = useAuthStore((s) => s.jtToken);
  const isTakeover = useAuthStore((s) => s.isTakeover);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();
  // Track the latest jtToken in a ref so the interval callback always
  // reads the current value without needing the interval to restart on
  // every token rotation.
  const tokenRef = useRef(jtToken);
  tokenRef.current = jtToken;

  useEffect(() => {
    if (!engageToken || !jtToken || isTakeover) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function check() {
      if (cancelled) return;
      const current = tokenRef.current;
      if (!current) return;
      const result = await validateJtToken(current);
      if (cancelled) return;
      if (result.valid === false) {
        // Definitively expired. Log out and redirect.
        clearAuth();
        // alert() is ugly but explicit — replace with a proper toast once
        // a global toast system is wired everywhere. For now, the explicit
        // message is more valuable than the polish.
        window.alert('Your JobTetris session expired. Please launch Engage again from JobTetris.');
        const loginPath = import.meta.env.DEV ? '/dev-login' : '/auth/callback';
        navigate(loginPath, { replace: true });
      } else if (result.valid === true && result.engage_access === false) {
        // The JT token is fine, but a JT admin switched off this user's Engage
        // access. No alert — the no-access page explains what happened. Wipe
        // the persisted session now; NoAccessPage clears in-memory auth on
        // mount (clearing it here would race AuthGuard's login redirect).
        useAuthStore.persist.clearStorage();
        navigate('/auth/no-access?reason=ENGAGE_ACCESS_REVOKED', { replace: true });
      }
      // valid === true (with access) → nothing to do
      // valid === null               → transient, try again next interval
    }

    // Stagger the first check so the dashboard render isn't competing
    // with a token-validation request for the first 10s.
    const start = setTimeout(() => {
      check();
      timer = setInterval(check, POLL_INTERVAL_MS);
    }, STARTUP_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(start);
      if (timer) clearInterval(timer);
    };
  }, [engageToken, jtToken, isTakeover, clearAuth, navigate]);
}
